import React, { useState, useRef, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useNotifications } from "../contexts/NotificationContext";
import { buildDetailNavigationState } from "../utils/navigation";

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications, markAsRead, clearNotifications, markAllAsRead } =
    useNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleNotificationClick = async (notification) => {
    // Mark as read first
    await markAsRead(notification.id);

    // Navigate based on notification type with error handling
    try {
      switch (notification.type) {
        case "new_ticket":
        case "ticket_reply":
        case "ticket_closed":
        case "ticket_assigned":
        case "ticket_updated":
          const ticketId =
            notification.ticket_number ||
            notification.ticketNumber ||
            notification.ticketId ||
            notification.id;

          if (ticketId) {
            navigate(`/tickets/${ticketId}`);
          }
          break;
        case "invoice":
          if (notification.invoiceId) {
            navigate(`/invoices/${notification.invoiceId}`, {
              state: buildDetailNavigationState(location),
            });
          }
          break;
        case "customer":
          if (notification.customerId) {
            navigate(`/customers/${notification.customerId}`);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error("Failed to navigate to notification");
    }

    setIsOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "new_ticket":
        return {
          icon: "fas fa-ticket-alt",
          halo: "bg-blue-500/10 ring-1 ring-blue-500/25 text-blue-600 dark:text-blue-400",
        };
      case "ticket_reply":
        return {
          icon: "fas fa-comment",
          halo: "bg-purple-500/10 ring-1 ring-purple-500/25 text-purple-600 dark:text-purple-400",
        };
      case "ticket_closed":
        return {
          icon: "fas fa-check-circle",
          halo: "bg-green-500/10 ring-1 ring-green-500/25 text-green-600 dark:text-green-400",
        };
      case "invoice":
        return {
          icon: "fas fa-file-invoice-dollar",
          halo: "bg-yellow-500/10 ring-1 ring-yellow-500/25 text-yellow-600 dark:text-yellow-400",
        };
      case "payment":
        return {
          icon: "fas fa-credit-card",
          halo: "bg-green-500/10 ring-1 ring-green-500/25 text-green-600 dark:text-green-400",
        };
      case "customer":
        return {
          icon: "fas fa-user-plus",
          halo: "bg-indigo-500/10 ring-1 ring-indigo-500/25 text-indigo-600 dark:text-indigo-400",
        };
      case "system":
        return {
          icon: "fas fa-cog",
          halo: "bg-gray-500/10 ring-1 ring-gray-500/25 text-gray-600 dark:text-gray-400",
        };
      default:
        return {
          icon: "fas fa-bell",
          halo: "bg-gray-500/10 ring-1 ring-gray-500/25 text-gray-600 dark:text-gray-400",
        };
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000); // difference in seconds

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;

    return time.toLocaleDateString();
  };

  return (
    <div className="relative group" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        className="relative p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className="fas fa-bell text-xl" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              animate={{ scale: 1, opacity: 1 }}
              className="absolute -top-1 -right-2 bg-gradient-to-br from-purple-500 to-pink-500 text-white text-[11px] rounded-full min-h-5 min-w-5 px-1.5 flex items-center justify-center font-semibold shadow-lg shadow-purple-500/40 ring-2 ring-white dark:ring-gray-900"
              exit={{ scale: 0, opacity: 0 }}
              initial={{ scale: 0, opacity: 0 }}
              key={unreadCount}
              transition={{ type: "spring", stiffness: 520, damping: 18 }}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Custom tooltip - only show when dropdown is closed */}
      {!isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
          <div className="bg-gray-800 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            {unreadCount > 0
              ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}`
              : "No new notifications"}
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-gray-800 dark:bg-gray-700 rotate-45" />
        </div>
      )}

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Mobile overlay backdrop */}
            {isMobile && (
              <motion.div
                animate={{ opacity: 1 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-md z-40 md:hidden"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
              />
            )}

            <motion.div
              animate={{ opacity: 1, y: 0, scale: 1 }}
              className={`${
                isMobile
                  ? "fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto"
                  : "absolute right-0 mt-2 w-96"
              } bg-white/95 dark:bg-gray-900/85 backdrop-blur-xl rounded-2xl shadow-2xl ring-1 ring-gray-200/60 dark:ring-white/[0.06] overflow-hidden z-50`}
              exit={{ opacity: 0, y: isMobile ? 20 : -8, scale: 0.96 }}
              initial={{ opacity: 0, y: isMobile ? 20 : -8, scale: 0.96 }}
              transition={{
                type: "spring",
                stiffness: 380,
                damping: 28,
              }}
            >
              {/* Header */}
              <div className="px-4 py-3 border-b border-gray-200/60 dark:border-white/[0.06]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold tracking-tight text-gray-900 dark:text-white">
                      Notifications
                    </h3>
                    {unreadCount > 0 && (
                      <motion.span
                        animate={{ scale: 1, opacity: 1 }}
                        className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-purple-500/10 text-purple-700 dark:text-purple-300 ring-1 ring-purple-500/30"
                        initial={{ scale: 0.4, opacity: 0 }}
                        key={unreadCount}
                        transition={{
                          type: "spring",
                          stiffness: 520,
                          damping: 18,
                        }}
                      >
                        {unreadCount} unread
                      </motion.span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {notifications.length > 0 && (
                      <button
                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title="Mark all as read"
                        onClick={markAllAsRead}
                      >
                        <i className="fas fa-check-double" />
                      </button>
                    )}
                    <button
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      onClick={() => setIsOpen(false)}
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Notifications List */}
              <div
                className={`${isMobile ? "max-h-[50vh]" : "max-h-96"} overflow-y-auto`}
              >
                {notifications.length === 0 ? (
                  <div className="px-4 py-10 text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 dark:bg-white/[0.05] ring-1 ring-gray-200/60 dark:ring-white/[0.06] rounded-full flex items-center justify-center">
                      <i className="fas fa-bell-slash text-xl text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      No notifications
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      You're all caught up
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {notifications.slice(0, 10).map((notification, idx) => {
                      const iconConfig = getNotificationIcon(notification.type);

                      return (
                        <motion.div
                          animate={{
                            opacity: 1,
                            y: 0,
                            filter: "blur(0px)",
                          }}
                          className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.04] cursor-pointer transition-colors ${
                            !notification.read
                              ? "bg-purple-500/5 dark:bg-purple-500/10"
                              : ""
                          }`}
                          initial={{
                            opacity: 0,
                            y: 6,
                            filter: "blur(4px)",
                          }}
                          key={notification.id}
                          transition={{
                            duration: 0.25,
                            delay: Math.min(idx * 0.03, 0.3),
                            ease: [0.32, 0.72, 0, 1],
                          }}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start gap-3">
                            {/* Icon halo */}
                            <div
                              className={`w-10 h-10 ${iconConfig.halo} rounded-xl flex items-center justify-center flex-shrink-0`}
                            >
                              <i className={`${iconConfig.icon} text-sm`} />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {notification.title}
                                  </p>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 truncate">
                                    {notification.message}
                                  </p>
                                  {notification.customer && (
                                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                      From: {notification.customer}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                    {formatTime(notification.timestamp)}
                                  </p>
                                </div>

                                {/* Unread indicator */}
                                {!notification.read && (
                                  <div className="ml-2 flex-shrink-0">
                                    <div className="w-2 h-2 bg-purple-500 rounded-full shadow-lg shadow-purple-500/50" />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Dismiss button */}
                            <button
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                            >
                              <i className="fas fa-times text-xs" />
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-200/60 dark:border-white/[0.06] bg-gray-50/60 dark:bg-white/[0.02]">
                  <div className="flex items-center justify-between">
                    <button
                      className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                      onClick={() => {
                        navigate("/notifications");
                        setIsOpen(false);
                      }}
                    >
                      View All
                    </button>
                    <button
                      className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      onClick={async () => {
                        if (notifications.length === 0) {
                          toast.info("No notifications to clear");
                        } else {
                          const result = await clearNotifications();

                          if (result !== false) {
                            toast.success("All notifications cleared");
                            setIsOpen(false);
                          } else {
                            toast.error("Failed to clear notifications");
                          }
                        }
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationDropdown;
